import { Modal, Tabs } from './index';
import PropertyGalleryStage from './PropertyGalleryStage';
import PropertyGoogleMap from './PropertyGoogleMap';
import PropertyStreetView from './PropertyStreetView';
import { formatPrice, getPropertyArea, getPropertyState } from '../propertyUtils';
import { useCurrency } from '../context/CurrencyContext';
import './PropertyFullscreenGallery.css';

// The fullscreen Images/Map/Street-view overlay (Zoopla-style), reached
// either by expanding the inline gallery or by clicking the static map
// preview further down the property page. Reuses ComponentLibrary's Modal
// (size="full") for backdrop/Escape/ARIA handling and Tabs for the tab
// list/panel - the bottom-tab-bar look is CSS-only (see
// PropertyFullscreenGallery.css), not a different component, since Tabs
// already owns both the list and the panel as one unit.
function PropertyFullscreenGallery({
  isOpen,
  onClose,
  initialTab = 'images',
  property,
  images,
  activeIndex,
  onActiveIndexChange,
  altText,
}) {
  const currencyCtx = useCurrency();
  if (!isOpen) {
    return null;
  }

  const latitude = Number(property?.latitude);
  const longitude = Number(property?.longitude);
  const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude);
  const listingType = property?.listing_type || property?.type || 'rent';
  const isShortStay = listingType === 'short-let' || listingType === 'hotel';
  const nightlyPrice = Number(property?.price_per_night || 0);
  const mainPrice = Number(property?.price || 0);

  const tabItems = [
    {
      id: 'images',
      label: 'Images',
      content: (
        <PropertyGalleryStage
          images={images}
          activeIndex={activeIndex}
          onActiveIndexChange={onActiveIndexChange}
          altText={altText}
          size="fullscreen"
        />
      ),
    },
    {
      id: 'map',
      label: 'Map',
      content: (
        <div className="property-fullscreen-gallery__map-layout">
          <PropertyGoogleMap
            properties={hasCoords ? [property] : []}
            variant="detail"
            className="property-fullscreen-gallery__map"
          />
          <aside className="property-fullscreen-gallery__side-panel">
            <p className="property-fullscreen-gallery__price">
              {isShortStay && nightlyPrice > 0
                ? `${formatPrice(nightlyPrice, currencyCtx)} / night`
                : formatPrice(mainPrice, currencyCtx)}
            </p>
            <p className="property-fullscreen-gallery__title">{property?.title}</p>
            <p className="property-fullscreen-gallery__location">
              {getPropertyArea(property) || 'Unknown area'}, {getPropertyState(property) || 'Nigeria'}
            </p>
            <dl className="property-fullscreen-gallery__facts">
              {property?.bedrooms ? (
                <div>
                  <dt>Bedrooms</dt>
                  <dd>{property.bedrooms}</dd>
                </div>
              ) : null}
              {property?.bathrooms ? (
                <div>
                  <dt>Bathrooms</dt>
                  <dd>{property.bathrooms}</dd>
                </div>
              ) : null}
            </dl>
            <button type="button" className="ui-button ui-button--secondary" onClick={onClose}>
              Back to listing
            </button>
          </aside>
        </div>
      ),
    },
    {
      id: 'street',
      label: 'Street view',
      content: hasCoords ? (
        <PropertyStreetView latitude={latitude} longitude={longitude} />
      ) : (
        <div className="property-map-shell">
          <div className="map-empty-state map-empty-state--standalone">
            <p>Street view isn&apos;t available for this property.</p>
            <small>The listing&apos;s address hasn&apos;t been pinned to a location yet.</small>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      className="property-fullscreen-modal"
      title={
        <button type="button" className="property-fullscreen-back" onClick={onClose}>
          ‹ Back to listing
        </button>
      }
    >
      <Tabs
        items={tabItems}
        defaultTabId={initialTab}
        key={initialTab}
        className="property-fullscreen-tabs"
      />
    </Modal>
  );
}

export default PropertyFullscreenGallery;
